import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { validateYupSchema } from "formik";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function PMListboxFormik(props) {
  const { label, options, name, values, setFieldValue, onChange, disabled } = props;

  const findSelectedById = (id) => {
    return options.find((option) => {
      const a = option.value;
      const b = id;
      if (b == null || b === "") return a == null || a === "";
      if (a == null || a === "") return false;
      return a === b || String(a) === String(b);
    });
  };

  const selected = props?.selected
    ? props?.selected
    : values
    ? values[name]
    : null;

  const [currentSelected, setCurrentSelected] = useState(null);

  const { t, i18n } = useTranslation();

  const resolveOptionLabel = (lbl) => {
    if (lbl == null || lbl === "") return t("listbox.none");
    if (typeof lbl === "string" && i18n.exists(lbl)) return t(lbl);
    return lbl;
  };

  const handleOnChange = (data) => {
    if (disabled) return;
    setCurrentSelected(data);
    if (setFieldValue) setFieldValue(name, data.value);
  };

  useEffect(() => {
    if (onChange) {
      onChange(currentSelected);
    }
  }, [currentSelected]);

  useEffect(() => {
    const next = findSelectedById(selected) ?? null;
    setCurrentSelected((prev) => {
      if (prev == null && next == null) return prev;
      if (prev == null || next == null) return next;
      const sameValue =
        prev.value === next.value ||
        (prev.value != null &&
          next.value != null &&
          String(prev.value) === String(next.value));
      if (sameValue && prev.label === next.label) return prev;
      return next;
    });
  }, [selected, options]);

  return (
    <Listbox name={name} value={currentSelected} onChange={handleOnChange} disabled={disabled}>
      {({ open }) => (
        <>
          <Listbox.Label className="block text-sm font-medium text-gray-700">
            {label}
          </Listbox.Label>
          <div className="relative mt-1">
            <Listbox.Button className={`relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}>
              <span className="flex items-center">
                {/* <img src={selected.avatar} alt="" className="h-6 w-6 flex-shrink-0 rounded-full" /> */}
                {currentSelected?.icon ? (
                  <currentSelected.icon className="w-5" />
                ) : (
                  ""
                )}
                               <span className="ml-3 block truncate">
                  {currentSelected ? resolveOptionLabel(currentSelected.label) : t("listbox.none")}
                </span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-[100] mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {options.map((option, index) => (
                  <Listbox.Option
                    key={option.id ?? option.value ?? index}
                    className={({ active }) =>
                      classNames(
                        active ? "text-white bg-indigo-600" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-3 pr-9"
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex items-center">
                          {option?.avatar ? (
                            <img
                              src={option.avatar}
                              alt=""
                              className="h-6 w-6 flex-shrink-0 rounded-full"
                            />
                          ) : option?.icon ? (
                            <option.icon className="w-5" />
                          ) : (
                            ""
                          )}
                          <span
                            className={classNames(
                              selected ? "font-semibold" : "font-normal",
                              "ml-3 block truncate"
                            )}
                          >
                            {resolveOptionLabel(option.label)}
                          </span>
                        </div>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
